import { useState } from 'react';

/**
 * Logic hook for the DocumentationPage
 * @returns {Object} Page logic and state
 */
export const useDocumentationLogic = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = {
    'getting-started': {
      title: 'Getting Started',
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white">Simple as Drag & Drop</h2>
          <p className="text-gray-300 text-lg">
            With elef89, there's no complex documentation to read through. Our platform is designed 
            with simplicity in mind - just drag and drop your app, and we'll handle everything else.
          </p>
          <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 mt-8">
            <div className="flex items-center justify-center mb-6">
              <svg className="w-16 h-16 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3 3m0 0l-3-3m3 3V8" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white text-center mb-4">Three Simple Steps</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <span className="text-teal-400 font-bold text-lg">1.</span>
                <p className="text-gray-300">Drag and drop your AAB or IPA file onto our platform</p>
              </div>
              <div className="flex items-start space-x-4">
                <span className="text-teal-400 font-bold text-lg">2.</span>
                <p className="text-gray-300">Let our AI analyze your app automatically</p>
              </div>
              <div className="flex items-start space-x-4">
                <span className="text-teal-400 font-bold text-lg">3.</span>
                <p className="text-gray-300">Get instant insights and publish with confidence</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    'features': {
      title: 'Why It Works',
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white">Let Us Do the Heavy Lifting</h2>
          <p className="text-gray-300 text-lg">
            We've built a powerful platform that handles all the complexity behind the scenes, 
            so you don't have to worry about documentation or technical details.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
              <h3 className="text-lg font-semibold text-teal-400 mb-3">Automated Analysis</h3>
              <p className="text-gray-300">
                Our AI automatically analyzes your app's permissions, security, and performance - no manual review needed.
              </p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
              <h3 className="text-lg font-semibold text-teal-400 mb-3">Instant Feedback</h3>
              <p className="text-gray-300">
                Get immediate insights about your app without reading through lengthy documentation.
              </p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
              <h3 className="text-lg font-semibold text-teal-400 mb-3">Smart Optimization</h3>
              <p className="text-gray-300">
                We automatically suggest improvements to help your app meet store requirements.
              </p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
              <h3 className="text-lg font-semibold text-teal-400 mb-3">Guided Publishing</h3>
              <p className="text-gray-300">
                Follow our simple, automated process to get your app published quickly and efficiently.
              </p>
            </div>
          </div>
        </div>
      )
    },
    'support': {
      title: 'Need Help?',
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white">We're Here to Help</h2>
          <p className="text-gray-300 text-lg">
            While our platform is designed to be self-explanatory, our support team is always 
            ready to assist if you need any help.
          </p>
          <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 mt-6">
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold text-white mb-4">Contact Support</h3>
              <p className="text-gray-300">
                Have questions? Our team is available 24/7 to help you succeed.
              </p>
            </div>
            <div className="flex flex-col md:flex-row justify-center items-center gap-6">
              <a href="mailto:davidd@elef89.com" 
                className="inline-flex items-center px-6 py-3 bg-teal-800 hover:bg-teal-700 transition-colors rounded-lg text-white"
                target="_blank"
                rel="noopener noreferrer">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Support
              </a>
            </div>
          </div>
        </div>
      )
    }
  };

  // Convert sections to the format expected by the InternalMenu component
  const menuItems = Object.entries(sections).map(([id, section]) => ({
    id,
    label: section.title
  }));

  return {
    activeSection,
    setActiveSection,
    sections,
    sidebarItems: menuItems // Keep the prop name for backwards compatibility
  };
}; 